import Vuex, { StoreOptions, MutationTree, GetterTree, ActionTree, ActionContext } from 'vuex'
import Vue from 'vue'
import { visualModule } from './modules/visual'
import { translationModule } from './modules/translation'
import { partyModule } from './modules/party'
import { chatModule } from './modules/chat'
import { userModule } from './modules/user'
import { newsletterModule } from './modules/newsletter'
import { competitionModule } from './modules/competition'
import { pageModulesModule } from './modules/page-modules'
import { playerModule } from './modules/player'
import { FrontendData } from '@/ts/interfaces/frontend-data'
import { Socket } from '@/socket'
import { Host } from '@/host'
import { cms } from '@/cms'

Vue.use(Vuex)

export interface RootState {
  isAdmin: boolean
  isMobile: boolean
  isError: boolean
  isFaqOpen: boolean
  isVideoOpen: boolean
  isCmsLoaded: boolean
  isCmsLoading: boolean
}

export const rootState: RootState = {
  isAdmin: false,
  isMobile: false,
  isError: false,
  isFaqOpen: false,
  isVideoOpen: false,
  isCmsLoaded: false,
  isCmsLoading: false
}

export const rootMutations: MutationTree<RootState> = {
  SET_IS_ADMIN(state: RootState, isAdmin) {
    state.isAdmin = isAdmin
  },
  SET_IS_MOBILE(state: RootState, isMobile) {
    state.isMobile = isMobile
  },
  SET_IS_ERROR(state: RootState, error) {
    state.isError = error
  },
  SET_IS_FAQ_OPEN(state: RootState, isOpen) {
    state.isFaqOpen = isOpen
  },
  SET_IS_VIDEO_OPEN(state: RootState, isOpen) {
    state.isVideoOpen = isOpen
  },
  SET_IS_CMS_LOADED(state: RootState, value: boolean) {
    state.isCmsLoaded = value
  },
  SET_IS_CMS_LOADING(state: RootState, value: boolean) {
    state.isCmsLoading = value
  }
}

export const rootGetters: GetterTree<RootState, any> = {
  isAdmin: (state: RootState) => state.isAdmin,
  stickerPath: (state: RootState) => new Host().stickerPath,
  isMobile: (state: RootState) => state.isMobile,
  isFaqOpen: (state: RootState) => state.isFaqOpen,
  isVideoOpen: (state: RootState) => state.isVideoOpen,
  isCmsLoaded: (state: RootState) => state.isCmsLoaded,
  frontendData: (state: RootState, g): FrontendData => ({
    competitionIsEnabled: g.competitionIsEnabled,
    pageModulesWaiting: g.pageModulesWaiting,
    pageModulesEnd: g.pageModulesEnd,
    partyId: g.partyId,
    partyIsWaitingRoomMinutes: g.partyIsWaitingRoomMinutes,
    partyStartAllowed: g.partyStartAllowed,
    translationFaqLocations: g.translationFaqLocations,
    visualIsDark: g.visualIsDark,
    visualPrimaryColor: g.visualPrimaryColor,
    visualBackgroundColor: g.visualBackgroundColor,
    visualArtistHeaderBackground: g.visualArtistHeaderBackground,
    visualArtistProfileBackground: g.visualArtistProfileBackground,
    visualSupportHeaderBackground: g.visualSupportHeaderBackground,
    visualSupportProfileBackground: g.visualSupportProfileBackground,
    visualUserHeaderBackground: g.visualUserHeaderBackground,
    visualUserProfileBackground: g.visualUserProfileBackground,
    visualOwnHeaderBackground: g.visualOwnHeaderBackground,
    visualOwnProfileBackground: g.visualOwnProfileBackground,
    visualMusicImageUrl: g.visualMusicImageUrl,
    visualHeaderImageUrl: g.visualHeaderImageUrl,
    visualRadioImageUrl: g.visualRadioImageUrl,
    visualPhoneImageUrl: g.visualPhoneImageUrl,
    visualLogoImageUrl: g.visualLogoImageUrl,
    visualFont: g.visualFont,
    visualStickerBackground: g.visualStickerBackground,
    visualStickersSelected: g.visualStickersSelected,
    visualHideStartPageGraphs: g.visualHideStartPageGraphs,
    visualHeaderElementsAsList: g.visualHeaderElementsAsList
  })
}

const rootActions: ActionTree<RootState, any> = {
  async INIT_CMS(ctx: ActionContext<RootState, any>) {
    if (ctx.state.isCmsLoaded || ctx.state.isCmsLoading) { // already done
      return
    }
    ctx.commit('SET_IS_CMS_LOADING', true)
    cms.onCampaignUrl((url: string) => ctx.commit('PARTY_SET_BASE_URL', url))
    await cms.init({ i18n: true, iframeEnabled: ctx.state.isAdmin, tracking: false })
    if (cms.campaign.app_id) {
      ctx.commit('PARTY_SET_ID', cms.campaign.app_id)
    }
    if (cms.campaign.url) {
      ctx.commit('PARTY_SET_BASE_URL', cms.campaign.url)
    }
    // Get start end dates from CMS
    if (cms.campaign.starts_at) {
      ctx.commit('PARTY_SET_STARTS_AT', new Date(cms.campaign.starts_at))
    }
    if (cms.campaign.ends_at) {
      ctx.commit('PARTY_SET_ENDS_AT', new Date(cms.campaign.ends_at))
    }
    if (!ctx.state.isAdmin) {
      // Redirect to pre page if token gating enabled and user lacks access
      cms.tokenGate.redirectIfBlocked()
    }
    ctx.commit('SET_IS_CMS_LOADED', true)
    ctx.commit('SET_IS_CMS_LOADING', false)
  },
  async INIT_APP(ctx: ActionContext<RootState, any>) {
    await ctx.dispatch('INIT_CMS')
    await ctx.dispatch('PARTY_GET_BY_ID')
    await ctx.dispatch('PLAYER_SERVICES_INIT')
  },
  MOBILE_DETECT(ctx: ActionContext<RootState, any>) {
    ctx.commit('SET_IS_MOBILE', window.innerWidth <= 900)
  },
  SOCKET_SETUP(ctx: ActionContext<RootState, any>) {
    return new Socket(ctx.getters.userToken, ctx.getters.partyId, () => ctx.commit('SET_IS_ERROR', true))
  },
  SET_DATA(ctx: ActionContext<RootState, any>, data: FrontendData) {
    if (!data) return

    if (data.competitionIsEnabled !== undefined) {
      ctx.commit('SET_COMPETITION_IS_ENABLED', data.competitionIsEnabled)
    }
    if (data.pageModulesWaiting !== undefined) {
      ctx.commit('SET_PAGE_MODULES_WAITING', data.pageModulesWaiting)
    }
    if (data.pageModulesEnd !== undefined) {
      ctx.commit('SET_PAGE_MODULES_END', data.pageModulesEnd)
    }
    if (data.partyIsWaitingRoomMinutes !== undefined) {
      ctx.commit('PARTY_SET_WAITING_ROOM_MINUTES', data.partyIsWaitingRoomMinutes)
    }
    if (data.partyStartAllowed !== undefined) {
      ctx.commit('PARTY_SET_START_ALLOWED', data.partyStartAllowed)
    }
    if (data.translationFaqLocations !== undefined) {
      ctx.commit('SET_TRANSLATION_FAQ_LOCATIONS', data.translationFaqLocations)
    }
    if (data.visualIsDark !== undefined) {
      ctx.commit('SET_VISUAL_IS_DARK', data.visualIsDark)
    }
    if (data.visualPrimaryColor !== undefined) {
      ctx.commit('SET_VISUAL_PRIMARY_COLOR', data.visualPrimaryColor)
    }
    if (data.visualBackgroundColor !== undefined) {
      ctx.commit('SET_VISUAL_BACKGROUND_COLOR', data.visualBackgroundColor)
    }
    if (data.visualArtistHeaderBackground !== undefined) {
      ctx.commit('SET_VISUAL_ARIST_HEADER_BACKGROUND', data.visualArtistHeaderBackground)
    }
    if (data.visualArtistProfileBackground !== undefined) {
      ctx.commit('SET_VISUAL_ARIST_PROFILE_BACKGROUND', data.visualArtistProfileBackground)
    }
    if (data.visualSupportHeaderBackground !== undefined) {
      ctx.commit('SET_VISUAL_SUPPORT_HEADER_BACKGROUND', data.visualSupportHeaderBackground)
    }
    if (data.visualSupportProfileBackground !== undefined) {
      ctx.commit('SET_VISUAL_SUPPORT_PROFILE_BACKGROUND', data.visualSupportProfileBackground)
    }
    if (data.visualUserHeaderBackground !== undefined) {
      ctx.commit('SET_VISUAL_USER_HEADER_BACKGROUND', data.visualUserHeaderBackground)
    }
    if (data.visualUserProfileBackground !== undefined) {
      ctx.commit('SET_VISUAL_USER_PROFILE_BACKGROUND', data.visualUserProfileBackground)
    }
    if (data.visualOwnHeaderBackground !== undefined) {
      ctx.commit('SET_VISUAL_OWN_HEADER_BACKGROUND', data.visualOwnHeaderBackground)
    }
    if (data.visualOwnProfileBackground !== undefined) {
      ctx.commit('SET_VISUAL_OWN_PROFILE_BACKGROUND', data.visualOwnProfileBackground)
    }
    if (data.visualMusicImageUrl !== undefined) {
      ctx.commit('SET_VISUAL_MUSIC_IMAGE_URL', data.visualMusicImageUrl)
    }
    if (data.visualHeaderImageUrl !== undefined) {
      ctx.commit('SET_VISUAL_HEADER_IMAGE_URL', data.visualHeaderImageUrl)
    }
    if (data.visualLogoImageUrl !== undefined) {
      ctx.commit('SET_VISUAL_LOGO_IMAGE_URL', data.visualLogoImageUrl)
    }
    if (data.visualRadioImageUrl !== undefined) {
      ctx.commit('SET_VISUAL_RADIO_IMAGE_URL', data.visualRadioImageUrl)
    }
    if (data.visualPhoneImageUrl !== undefined) {
      ctx.commit('SET_VISUAL_PHONE_IMAGE_URL', data.visualPhoneImageUrl)
    }
    if (data.visualFont !== undefined) {
      ctx.commit('SET_VISUAL_FONT', data.visualFont)
    }
    if (data.visualStickerBackground !== undefined) {
      ctx.commit('SET_STICKER_BACKGROUND', data.visualStickerBackground)
    }
    if (data.visualStickersSelected !== undefined) {
      ctx.commit('SET_STICKER_SELECTED', data.visualStickersSelected)
    }
    if (data.visualHideStartPageGraphs !== undefined) {
      ctx.commit('SET_VISUAL_HIDE_START_PAGE_GRAPHS', data.visualHideStartPageGraphs)
    }
    if (data.visualHeaderElementsAsList !== undefined) {
      ctx.commit('SET_VISUAL_HEADER_ELEMENTS_AS_LIST', data.visualHeaderElementsAsList)
    }
  },
  // SAVE(ctx: ActionContext<RootState, any>) {
  //   wordpress.saveData({ partyId: ctx.getters.partyId })
  // },
  // EXIT() {
  //   wordpress.exit()
  // }
}

const store: StoreOptions<RootState> = {
  state: rootState,
  mutations: rootMutations,
  getters: rootGetters,
  actions: rootActions,
  modules: {
    partyModule,
    visualModule,
    translationModule,
    chatModule,
    userModule,
    newsletterModule,
    competitionModule,
    pageModulesModule,
    playerModule
  },
}

export default () => (new Vuex.Store(store))
